import { Injectable, Inject, PLATFORM_ID, output } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
declare global {
  interface Window {
    MathJax?: any;
  }
}
export interface MathJaxConfig {
  ready: Promise<void>;
  typeset: (element?: HTMLElement) => Promise<void>;
  typesetClear: (element?: HTMLElement) => Promise<void>;
}
/**
* Service for configuring and initializing MathJax 4 rendering engine via CDN.
*
* - Loads MathJax only once (shared Promise)
* - Works with TeX + MathML input
* - Uses SVG output by default
* - Disables menu / explorer / a11y interactive features
* - SSR-safe (no window/document access on the server)
*/
@Injectable({ providedIn: 'root' })
export class MathJaxConfigService {
  private _mathJaxLoaded = false;
  private _loadPromise?: Promise<void>;
  private readonly _isBrowser: boolean;
  private readonly _defaultScale = 0.98;
  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this._isBrowser = isPlatformBrowser(platformId);
  }
  /**
   * Initialize MathJax 4 from CDN with custom configuration.
   * @param scale Scaling factor for equations (use higher in tables, etc.)
   */
  public initialize(scale = this._defaultScale): MathJaxConfig {
    // Ensure MathJax load has been triggered
    const ready = this.loadMathJax(scale);
    return {
      ready,
      typeset: (element?: HTMLElement) => this.typeset(element),
      typesetClear: (element?: HTMLElement) => this.typesetClear(element),
    };
  }
  /**
   * Initialize with MathML input support (currently same as initialize).
   * You can customize this if you want different configs or URLs.
   */
  public initializeWithMathML(scale = this._defaultScale): MathJaxConfig {
    return this.initialize(scale);
  }
  // ----------------- Private helpers -----------------
  private loadMathJax(scale: number): Promise<void> {
    // Server-side: no-op, but pretend it's "loaded"
    if (!this._isBrowser) {
      this._mathJaxLoaded = true;
      return Promise.resolve();
    }
    // Already loaded
    if (this._mathJaxLoaded) {
      return Promise.resolve();
    }
    // Already loading
    if (this._loadPromise) {
      return this._loadPromise;
    }
    this._loadPromise = new Promise<void>((resolve, reject) => {
      // Configure MathJax 4 before loading the script
      window.MathJax = {
        loader: {
          load: ['input/tex', 'input/mml', 'output/svg', '[tex]/configmacros', '[tex]/cancel'],
          paths: {
            '[tex]': '/assets/mathjax/input/tex/extensions'
          }
        },
        tex: {
          inlineMath: [['\\(', '\\)']],
          displayMath: [['\\[', '\\]']],
          processEscapes: true,
          processEnvironments: true,
          packages: { '[+]': ['base', 'ams', 'newcommand', 'autoload', 'configmacros', 'cancel'] },
          macros: {
            // Workaround for \cline: define it as \hline (loses column specificity)
            cline: ['\\hline', 1],
            // Workaround for \multicolumn: just return the content (loses spanning)
            multicolumn: ['{#3}', 3], // Takes 1 arg (ignores num and align)
          }
        },
        mml: {
          // Enable MathML input processing
          parseAs: 'html',
          forceReparse: true,
        },
        svg: {
          fontCache: 'global',
          scale,
          minScale: 0.5,
          mtextInheritFont: false,
          merrorInheritFont: true,
          mathmlSpacing: false,
          skipAttributes: {},
          exFactor: 0.5,
          displayAlign: 'center',
          displayIndent: '0',
        },
        output: {
          displayOverflow: 'scroll'
        },
        // Disable interactive / a11y / menu features
        options: {
          skipHtmlTags: ['script', 'noscript', 'style', 'textarea', 'pre', 'code'],
          renderActions: {
            addMenu: [],
            checkLoading: [],
          },
        },
        a11y: {
          speech: false,
          subtitles: false,
          viewBox: false,
          backgroundColor: 'none',
          foregroundColor: 'none',
          speechRules: 'none',
        },
        startup: {
          typset: false,
          ready: () => {
            console.log('MathJax 4 is ready');
            this._mathJaxLoaded = true;
            // Call default startup
            window.MathJax.startup.defaultReady();
            // Try to strip remaining interactive features
            try {
              const startupDoc = window.MathJax.startup.document;
              startupDoc.options.enableMenu = false;
              startupDoc.options.enableExplorer = false;
              startupDoc.options.enableBraille = false;
              startupDoc.options.enableEnrichment = false;
              startupDoc.options.enableSpeech = false;

              // Disable a11y helpers/explorer
              if (window.MathJax.a11y) {
                window.MathJax.a11y.explorer = null;
                window.MathJax.a11y.assistiveMml = null;
                window.MathJax.a11y.sre = null;
              }
              console.log('MathJax interactive features disabled');
            } catch (e) {
              console.warn('Could not disable some MathJax features:', e);
            }
            resolve();
          },
          pageReady: () => {
            // Let MathJax do its default initial page typeset
            return window.MathJax.startup.defaultPageReady();
          },
        },
        // CommonHTML as fallback (if ever used)
        chtml: {
          scale,
          matchFontHeight: true,
          mtextInheritFont: false,
        },
        };
      // Load MathJax 4 from CDN - tex-mml-svg supports TeX + MathML input, SVG output
      const script = document.createElement('script');
      script.src = 'assets/mathjax/tex-mml-svg.js';
      script.async = true;
      script.onload = () => {
        console.log('MathJax 4 script loaded');
      };
      script.onerror = () => {
        console.error('Failed to load MathJax 4');
        reject(new Error('Failed to load MathJax 4'));
      };
      document.head.appendChild(script);
    });
    return this._loadPromise;
  }

  public async typeset(element?: HTMLElement): Promise<void> {
    if (!this._isBrowser) {
      return;
    }
    // Ensure we have at least started loading MathJax
    if (!this._loadPromise) {
      this._loadPromise = this.loadMathJax(this._defaultScale);
    }
    try {
      await this._loadPromise;
      const mj = window.MathJax;
      if (mj?.typesetPromise) {
        await mj.typesetPromise(element ? [element] : undefined);
        console.log('MathJax 4 typeset completed');
      } else {
        console.warn('MathJax typesetPromise not available');
      }
    } catch (error) {
      console.error('MathJax 4 typeset error:', error);
    }
  }

  public async typesetClear(element?: HTMLElement): Promise<void> {
    if (!this._isBrowser) {
      return;
    }
    // If MathJax isn't loaded yet, there's nothing to clear
    if (!window.MathJax || !window.MathJax.typesetClear) {
      return;
    }
    try {
      window.MathJax.typesetClear(element ? [element] : undefined);
    } catch (error) {
      console.error('MathJax 4 typesetClear error:', error);
    }
  }
}
